<script setup lang="ts">
import { computed, ref } from 'vue'
import * as linkify from 'linkifyjs'
import { useElementHover } from '@vueuse/core'
import { useModal } from '@/hooks/useModal'
import { truncate } from '@/libs/utilities/truncate'
import { nanoid } from 'nanoid'
import {
  useUpdateKollegeUserInstructorMemoMutation,
  type OkaneKollegeUserPageSidebarMemoFragment,
} from './OkaneKollegeUserPageSidebarMemo.generated'
import OkaneKollegeUserPageSidebarMemoModal from './OkaneKollegeUserPageSidebarMemoModal.vue'
import { useAlert } from '@/libs/alertPortal'

const props = defineProps<{
  fragment: OkaneKollegeUserPageSidebarMemoFragment
}>()

const memo = ref<string>(props.fragment.instructorMemo)

type Block =
  | {
      renderingKey: string
      type: 'text'
      text: string
    }
  | {
      renderingKey: string
      type: 'url'
      url: string
    }
  | {
      renderingKey: string
      type: 'line_break'
    }

const blocks = computed<Block[]>(() => {
  if (!memo.value) return []

  return memo.value
    .trim()
    .split('\n')
    .flatMap<Block>(line => {
      if (line === '') {
        return [{ type: 'line_break', renderingKey: nanoid() }]
      }

      const tokens = linkify.tokenize(line)
      const blocks = tokens.map<Block>(t => {
        if (t.isLink) {
          return { type: 'url', url: t.toHref(), renderingKey: nanoid() }
        }

        return { type: 'text', text: t.toString(), renderingKey: nanoid() }
      })

      return [...blocks, { type: 'line_break', renderingKey: nanoid() }]
    })
})

const containerEl = ref<HTMLDivElement | null>(null)
const isContainerElHovered = useElementHover(containerEl)

const EditModal = useModal()

const handleClickMemoBlock = () => {
  EditModal.openModal()
}

const alert = useAlert()

const updateKollegeUserInstructorMemoMutation =
  useUpdateKollegeUserInstructorMemoMutation()

const handleUpdateMemo = async (newMemo: string) => {
  try {
    await updateKollegeUserInstructorMemoMutation.mutate({
      input: {
        id: props.fragment.id,
        memo: newMemo,
      },
    })

    memo.value = newMemo

    EditModal.closeModal()
    alert.success('メモを更新しました')
  } catch (error) {
    console.error(error)
    alert.error('メモの更新に失敗しました')
  }
}
</script>

<template>
  <div ref="containerEl" :class="$style.wrapper" @click="handleClickMemoBlock">
    <div :class="$style.header">
      <div :style="{ fontWeight: 'bold' }">メモ</div>
      <p v-if="isContainerElHovered" :class="$style.editText">編集</p>
    </div>
    <div :class="$style.content" @click="handleClickMemoBlock">
      <div v-if="blocks.length > 0">
        <template v-for="block in blocks" :key="block.renderingKey">
          <template v-if="block.type === 'text'">
            <span>{{ block.text }}</span>
          </template>
          <template v-else-if="block.type === 'url'">
            <a :href="block.url" target="_blank" @click.stop>{{
              truncate(block.url, 80)
            }}</a>
          </template>
          <template v-else-if="block.type === 'line_break'">
            <br />
          </template>
        </template>
      </div>
      <p v-else :class="$style.noMemo">記入なし</p>
    </div>
    <EditModal.ControlledModal disableBackdrop>
      <OkaneKollegeUserPageSidebarMemoModal
        :initial-memo="memo"
        @update="handleUpdateMemo"
        @close="EditModal.closeModal"
      />
    </EditModal.ControlledModal>
  </div>
</template>

<style lang="scss" module>
.wrapper {
  font-size: 13px;
  cursor: pointer;
}

.header {
  display: flex;
  justify-content: space-between;
}

.content {
  margin-top: 8px;
  white-space: pre-wrap;
  word-break: break-all;
}

.editText {
  color: $color-text-and-object-accent-primary;

  &:hover {
    text-decoration: underline;
  }
}

.noMemo {
  color: $color-text-and-object-low-emphasis;
}
</style>
